import {
  Chords,
  GUITAR,
  Notes,
  UKULELE,
  defaultTuning,
  getStrum,
  shapeFor,
  shapePitches,
  strumOnsets,
  type Meter,
  type PlaybackEvent,
  type PlaybackSpec,
  type SoundId,
} from '@audigoal/core';

/**
 * Backing-track generator (plan doc 10). Turns a chord chart into a few
 * playable tracks: strummed or arpeggiated chords voiced with real shapes,
 * a bass line, drums and an optional pad. Styles are deliberately simple;
 * the point is a steady, in-tune band to play along with.
 */

export interface ChartChord {
  symbol: string;
  startBeat: number;
  durationBeats: number;
}

export type BackingStyle =
  | 'strum-island'
  | 'strum-folk'
  | 'strum-waltz'
  | 'arpeggio'
  | 'pad'
  | 'blues-shuffle'
  | 'swing-comp'
  | 'bossa';

export interface BackingOptions {
  meter: Meter;
  tempo: number;
  style: BackingStyle;
  /** Chord instrument. */
  instrument?: 'ukulele' | 'guitar' | 'piano';
  drums?: boolean;
  bass?: boolean;
  pad?: boolean;
  countIn?: number;
  loop?: boolean;
}

export interface BackingTrackSpec {
  id: 'chords' | 'bass' | 'drums' | 'pad';
  spec: PlaybackSpec;
  gain: number;
}

export interface Backing {
  tracks: BackingTrackSpec[];
  lengthBeats: number;
  tempo: number;
  meter: Meter;
}

const STYLE_STRUM: Partial<Record<BackingStyle, string>> = {
  'strum-island': 'strum.island',
  'strum-folk': 'strum.d-du',
  'strum-waltz': 'strum.waltz',
  'blues-shuffle': 'strum.shuffle',
  bossa: 'strum.calypso',
  'swing-comp': 'strum.d4',
};

function chordVoicing(symbol: string, instrument: BackingOptions['instrument']): number[] {
  if (instrument === 'ukulele' || instrument === 'guitar') {
    const profile = instrument === 'ukulele' ? UKULELE : GUITAR;
    const shape = shapeFor(symbol, instrument);
    const tuning = defaultTuning(profile);
    if (shape !== null && tuning !== undefined) return shapePitches(shape, tuning);
  }
  const parsed = Chords.parseSymbol(symbol);
  const root = { ...parsed.root, octave: 3 };
  return Chords.chordNotes(root, parsed.chordId).map(Notes.midiOf);
}

function rootMidi(symbol: string, octave: number): number {
  const parsed = Chords.parseSymbol(symbol);
  return Notes.midiOf({ ...parsed.root, octave });
}

function fifthMidi(symbol: string, octave: number): number {
  const parsed = Chords.parseSymbol(symbol);
  const notes = Chords.chordNotes({ ...parsed.root, octave }, parsed.chordId);
  return Notes.midiOf(notes[2] ?? notes[0]!);
}

export function buildBacking(chart: readonly ChartChord[], options: BackingOptions): Backing {
  const { meter, tempo, style } = options;
  const instrument = options.instrument ?? 'ukulele';
  const chordSound: SoundId =
    instrument === 'piano' ? 'piano' : instrument === 'guitar' ? 'guitar-steel' : 'ukulele';
  const beatsPerBar = (meter.beats * 4) / meter.unit;
  const lengthBeats = Math.max(...chart.map((c) => c.startBeat + c.durationBeats), beatsPerBar);
  const swing = style === 'blues-shuffle' || style === 'swing-comp' ? 0.66 : 0;

  const chords: PlaybackEvent[] = [];
  const bass: PlaybackEvent[] = [];
  const drums: PlaybackEvent[] = [];
  const pad: PlaybackEvent[] = [];

  const strumId = STYLE_STRUM[style];
  const pattern = strumId !== undefined ? getStrum(strumId) : null;
  const patternOnsets = pattern !== null ? strumOnsets(pattern) : [];
  const patternLength =
    pattern !== null ? (pattern.meter.beats * 4) / pattern.meter.unit : beatsPerBar;

  for (const chord of chart) {
    const voicing = chordVoicing(chord.symbol, instrument);
    const end = chord.startBeat + chord.durationBeats;

    // Chords: strum pattern repeated across the chord's span, or arpeggio/pad.
    if (style === 'arpeggio') {
      const order = [0, 1, 2, 3, 2, 1];
      for (let b = chord.startBeat; b < end; b += 0.5) {
        const i = Math.round((b - chord.startBeat) * 2) % order.length;
        const midi = voicing[(order[i] ?? 0) % voicing.length] as number;
        chords.push({ at: b, dur: 0.5, midi: [midi], vel: 0.7 });
      }
    } else if (style === 'pad') {
      chords.push({ at: chord.startBeat, dur: chord.durationBeats, midi: voicing, vel: 0.5 });
    } else if (pattern !== null) {
      for (let barStart = chord.startBeat; barStart < end; barStart += patternLength) {
        for (const onset of patternOnsets) {
          const at = barStart + onset;
          if (at >= end) break;
          const token = pattern.grid[Math.round(onset * 2)] ?? 'D';
          chords.push({
            at,
            dur: 0.45,
            midi: token === 'x' ? [] : voicing,
            vel: token === 'U' ? 0.55 : 0.8,
          });
        }
      }
    }

    // Bass: root on the strong beats, fifth on beat 3 in folk, walking in swing.
    if (options.bass !== false) {
      const root = rootMidi(chord.symbol, 2);
      const fifth = fifthMidi(chord.symbol, 2);
      if (style === 'swing-comp') {
        const walk = [root, root + 4, fifth, fifth - 1];
        for (let b = chord.startBeat, i = 0; b < end; b += 1, i += 1) {
          bass.push({ at: b, dur: 0.9, midi: [walk[i % walk.length] as number], vel: 0.7 });
        }
      } else if (style === 'strum-waltz') {
        for (let b = chord.startBeat; b < end; b += 3)
          bass.push({ at: b, dur: 1.5, midi: [root], vel: 0.75 });
      } else {
        for (let b = chord.startBeat; b < end; b += 2) {
          const onThree = (b - chord.startBeat) % 4 === 2;
          bass.push({
            at: b,
            dur: 1.5,
            midi: [onThree && style === 'strum-folk' ? fifth : root],
            vel: 0.75,
          });
        }
      }
    }

    // Pad: sustained triad, quiet.
    if (options.pad === true && style !== 'pad') {
      pad.push({
        at: chord.startBeat,
        dur: chord.durationBeats,
        midi: voicing.slice(0, 3).map((m) => m + 12),
        vel: 0.3,
      });
    }
  }

  // Drums per bar.
  if (options.drums !== false) {
    for (let bar = 0; bar * beatsPerBar < lengthBeats; bar += 1) {
      const start = bar * beatsPerBar;
      if (style === 'strum-waltz') {
        drums.push({ at: start, dur: 0.25, midi: [36], vel: 0.9 });
        drums.push({ at: start + 1, dur: 0.25, midi: [42], vel: 0.5 });
        drums.push({ at: start + 2, dur: 0.25, midi: [42], vel: 0.5 });
      } else if (style === 'bossa') {
        [0, 1.5, 3].forEach((b) => drums.push({ at: start + b, dur: 0.25, midi: [36], vel: 0.8 }));
        for (let b = 0; b < beatsPerBar; b += 0.5)
          drums.push({ at: start + b, dur: 0.2, midi: [42], vel: 0.35 });
        [1, 2.5].forEach((b) => drums.push({ at: start + b, dur: 0.25, midi: [37], vel: 0.5 }));
      } else {
        for (let b = 0; b < beatsPerBar; b += 1) {
          drums.push({
            at: start + b,
            dur: 0.25,
            midi: [b % 2 === 0 ? 36 : 38],
            vel: b % 2 === 0 ? 0.9 : 0.7,
          });
        }
        for (let b = 0; b < beatsPerBar; b += 0.5)
          drums.push({ at: start + b, dur: 0.2, midi: [42], vel: 0.4 });
      }
    }
  }

  const base = {
    kind: 'playback' as const,
    tempo,
    ...(options.countIn !== undefined ? { countIn: options.countIn } : {}),
    ...(options.loop === true ? { loop: true } : {}),
    ...(swing > 0 ? { swing } : {}),
  };
  const tracks: BackingTrackSpec[] = [
    {
      id: 'chords',
      gain: 0.9,
      spec: {
        ...base,
        instrument: chordSound,
        events: chords,
        ...(pattern !== null ? { strum: { direction: 'down', spreadMs: 16 } } : {}),
      },
    },
  ];
  if (bass.length > 0)
    tracks.push({ id: 'bass', gain: 0.8, spec: { ...base, instrument: 'bass', events: bass } });
  if (drums.length > 0)
    tracks.push({ id: 'drums', gain: 0.6, spec: { ...base, instrument: 'drums', events: drums } });
  if (pad.length > 0)
    tracks.push({ id: 'pad', gain: 0.4, spec: { ...base, instrument: 'strings', events: pad } });

  return { tracks, lengthBeats, tempo, meter };
}
